import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, ChevronDown } from "lucide-react";
import { ShieldMark } from "./ShieldMark";
import { ThemeToggle } from "./ThemeToggle";
import { MobileNavigation } from "./MobileNavigation";
import { getTools } from "../../data/tools";
import { useLang } from "../../i18n/LanguageContext";

const linkClass = ({ isActive }) =>
  `text-[11px] font-bold uppercase tracking-[0.14em] transition-colors duration-200 ${
    isActive ? "text-onix-white border-b-2 border-onix-blue pb-0.5" : "text-onix-silver hover:text-onix-white"
  }`;

const langBtnClass = (active) =>
  active
    ? "text-onix-white underline underline-offset-4 decoration-onix-blue decoration-2"
    : "text-onix-gray hover:text-onix-silver transition-colors";

export const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const { lang, t, setLang } = useLang();
  const tools = getTools(lang);
  const nav = [
    { key: "soluciones", label: t.nav.soluciones, to: "/soluciones" },
    { key: "seguridad", label: t.nav.seguridad, to: "/seguridad-y-gobierno" },
    { key: "empresa", label: t.nav.empresa, to: "/empresa" },
  ];

  useEffect(() => {
    setToolsOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setToolsOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setToolsOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <>
      <header
        data-testid="header"
        className="fixed top-0 left-0 right-0 z-50 header-glass border-b border-onix-line"
      >
        <div className="onix-shell flex items-center justify-between h-[76px]">
          <Link data-testid="header-logo-link" to="/" className="flex items-center gap-4 shrink-0" aria-label="ONIX">
            <ShieldMark variant="white" className="w-[52px] md:w-[60px]" eager alt="Escudo ONIX" />
            <span className="hidden sm:flex flex-col leading-none">
              <span className="font-sans font-bold text-base tracking-[0.3em] text-onix-white">ONIX</span>
              <span className="tech-label mt-1.5 !text-[9px]">{t.header.tagline}</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8" aria-label={t.header.mainNav}>
            <NavLink data-testid="nav-ecosistema" to="/ecosistema" className={linkClass}>
              {t.nav.ecosistema}
            </NavLink>
            <div className="relative" ref={dropdownRef}>
              <button
                data-testid="nav-herramientas"
                className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] transition-colors duration-200 ${
                  location.pathname.startsWith("/herramientas") ? "text-onix-white" : "text-onix-silver hover:text-onix-white"
                }`}
                aria-expanded={toolsOpen}
                aria-haspopup="true"
                onClick={() => setToolsOpen((v) => !v)}
              >
                {t.nav.herramientas}
                <ChevronDown size={12} className={`transition-transform duration-200 ${toolsOpen ? "rotate-180" : ""}`} aria-hidden="true" />
              </button>
              {toolsOpen && (
                <div
                  data-testid="tools-dropdown"
                  className="absolute left-1/2 -translate-x-1/2 top-full mt-5 w-[380px] bg-onix-black2 border border-onix-line p-2"
                >
                  {tools.map((tool) => (
                    <Link
                      key={tool.slug}
                      data-testid={`dropdown-${tool.slug}`}
                      to={`/herramientas/${tool.slug}`}
                      className="flex items-baseline gap-4 px-4 py-3 transition-colors hover:bg-onix-graphite"
                    >
                      <span className="tech-label">{tool.num}</span>
                      <span className="flex flex-col">
                        <span className="text-xs font-bold uppercase tracking-[0.1em] text-onix-white">{tool.name}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            {nav.map((item) => (
              <NavLink key={item.key} data-testid={`nav-${item.key}`} to={item.to} className={linkClass}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3 md:gap-5">
            <div
              className="hidden md:flex items-center gap-2 text-[11px] font-bold tracking-[0.14em]"
              role="group"
              aria-label={t.header.langSelector}
            >
              <button data-testid="lang-es" onClick={() => setLang("es")} aria-pressed={lang === "es"} className={langBtnClass(lang === "es")}>
                ES
              </button>
              <span className="text-onix-gray" aria-hidden="true">
                /
              </span>
              <button data-testid="lang-en" onClick={() => setLang("en")} aria-pressed={lang === "en"} className={langBtnClass(lang === "en")}>
                EN
              </button>
            </div>
            <ThemeToggle />
            <Link data-testid="header-cta" to="/contacto" className="btn-onix-primary !px-5 !py-2.5 max-md:!hidden">
              {t.header.cta}
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-onix-white p-2 -mr-2"
              aria-label={t.header.openMenu}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen(true)}
            >
              <Menu size={22} aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>
      <MobileNavigation open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </>
  );
};
